// @ts-strict-ignore
import React, { useCallback, useMemo } from 'react'
import moment from 'moment'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import styled from 'styled-components/native'

import Typography from 'APP/Converse/Typography'
import I18n from 'APP/I18n'
import { Metrics } from 'APP/Themes'
import { selectSelectedDay } from 'APP/Store/ProviderBooking/selectors'
import { DATE_FORMAT } from 'APP/Redux/AppointmentBookingRedux'
import { providerBookingActions } from 'APP/Store/ProviderBooking'

import DayTabWithAvailability from '../common/dayTabWithAvailability'
import { createDays } from '../utils/helpers'
import { AvailabilitiesExists } from '../utils/types'
import Analytics from 'APP/Services/Analytics'

const Wrapper = styled.View`
  padding: ${Metrics.baseMargin * 1.5}px 0;
  background-color: ${({ theme }) => theme.colors.practitionerMsgBg};
`

const StyledScrollView = styled.ScrollView.attrs({
  contentContainerStyle: {
    paddingTop: 0,
    paddingHorizontal: Metrics.baseMargin * 1.5,
    paddingBottom: 0,
  },
})``

const Header = styled(Typography)`
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: ${({ theme }) => `${theme.metrics.baseMargin}px`};
`

interface Props {
  dateFrom: string
  dateUntil: string
  availabilitiesExists: Readonly<AvailabilitiesExists>
}

export const DayPicker = ({ dateFrom, dateUntil, availabilitiesExists }: Props) => {
  const dispatch = useDispatch()
  const selectedDay = useSelector(selectSelectedDay)

  const calendarFormat = I18n.t('AppointmentBooking.timeslot_formats', {
    returnObjects: true,
  }) as moment.CalendarSpec

  const handleSelectDay = useCallback(
    (day: string) => {
      dispatch(providerBookingActions.selectDay(day))
    },
    [dispatch]
  )

  const onSelectDay = (formattedDay: string) => {
    handleSelectDay(formattedDay)
    Analytics.trackEvent('button_click', {
      button_value: 'calendar_date',
    })
  }

  const selectedDate = moment(selectedDay, DATE_FORMAT)
  const days = useMemo(() => createDays(moment(dateFrom), moment(dateUntil)), [dateFrom, dateUntil])

  return (
    <Wrapper>
      <Header variant="bodyNormalBold" align="center">
        {selectedDate.calendar(calendarFormat)}
      </Header>
      <StyledScrollView horizontal centerContent>
        {days.map((day) => {
          const formattedDay = day.format(DATE_FORMAT)
          const selected = day.isSame(selectedDate, 'day')
          const hasAvailability = availabilitiesExists[formattedDay] ?? false

          return (
            <DayTabWithAvailability
              key={formattedDay}
              day={day}
              onSelect={() => onSelectDay(formattedDay)}
              selected={selected}
              hasAvailabilities={hasAvailability}
            />
          )
        })}
      </StyledScrollView>
    </Wrapper>
  )
}
